import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";

import {
    type FundExplorerApiData,
    type FundExplorerDataIndexed,
} from "@hsl/fund-explorer/types";

import { type Config } from "../../config/types";
import { useAllTranslations } from "../../hooks/useTranslation";
import { createExplorerStore } from "../../store/store";

interface Props {
    collectivesConfig: Config;
    etfConfig: Config;
    pmcConfig: Config;
    subscribedFunds: string[];
}

export const useFundCentres = ({
    collectivesConfig,
    etfConfig,
    pmcConfig,
    subscribedFunds,
}: Props) => {
    const translations = useAllTranslations();

    const baseUrl = useMemo(() => {
        const scripts = Array.from(
            document.querySelectorAll<HTMLScriptElement>("head script"),
        );
        const baseUrl = scripts
            .find((script) => script.src.includes("lgim/js/fundExplorer.js"))
            ?.src.slice(0, -"lgim/js/fundExplorer.js".length)
            ?.replace("localhost:8888", "localhost:9999");

        if (!baseUrl) throw new Error("Could not find base url");
        return baseUrl;
    }, []);

    const groupedFundIds = subscribedFunds.reduce(
        (acc, sub) => {
            const [type, id] = sub.split("-") as [
                "collective" | "pmc" | "etf",
                string,
            ];
            if (!acc[type]) {
                console.warn(`Unknown fund type ${type}`);
                return acc;
            }
            if (!acc[type].includes(id)) {
                acc[type].push(id);
            }
            return acc;
        },
        {
            collective: [] as string[],
            pmc: [] as string[],
            etf: [] as string[],
        },
    );

    const fundCentreConfigs: {
        type: "collective" | "pmc" | "etf";
        config: Config;
        funds: string[];
    }[] = [];

    if (groupedFundIds.collective.length > 0 && collectivesConfig) {
        fundCentreConfigs.push({
            type: "collective",
            config: collectivesConfig,
            funds: groupedFundIds.collective,
        });
    }
    if (groupedFundIds.pmc.length > 0 && pmcConfig) {
        fundCentreConfigs.push({
            type: "pmc",
            config: pmcConfig,
            funds: groupedFundIds.pmc,
        });
    }
    if (groupedFundIds.etf.length > 0 && etfConfig) {
        fundCentreConfigs.push({
            type: "etf",
            config: etfConfig,
            funds: groupedFundIds.etf,
        });
    }

    return useQueries({
        queries: fundCentreConfigs.map(({ type, config, funds }) => ({
            queryKey: ["filtered-fund-center", type, funds],
            queryFn: async () => {
                const url = new URL(baseUrl + ".netlify/functions/fund-centre");
                const fcId = config.fundCentreId.find((x) =>
                    x.audiences.includes(config.audienceId),
                );
                if (!fcId)
                    throw new Error("No fund centre id found for audience");
                url.searchParams.set("id", String(fcId.id));
                url.searchParams.set("audience", String(config.audienceId));
                url.searchParams.set("language", String(config.languageId));
                url.searchParams.set("funds", funds.join(","));
                const response = await fetch(url);
                const data: {
                    metadata: FundExplorerApiData["metadata"];
                    indexedData: FundExplorerDataIndexed;
                } = await response.json();
                const store = createExplorerStore();
                // while most translations use a separate context, this is used
                // for translating the CSV download
                store.getState().setTranslations(translations);
                await store.getState().init(config, data);

                return {
                    type,
                    funds,
                    store,
                };
            },
            staleTime: Infinity,
            refetchOnWindowFocus: false,
            keepPreviousData: true,
        })),
    });
};

export type FundCentres = ReturnType<typeof useFundCentres>;
